// libs/frontend/feature-public/src/lib/components/dynamic/barra-navegacion/barra-navegacion.component.ts
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  Renderer2,
  computed,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { AuthStore } from '@front/frontend/data-access-auth/stores/auth.store';
import { StorageService } from '@irontec/ngx-encrypted-storage';
import { LogOut, LucideAngularModule, Menu, X } from 'lucide-angular';
import { filter } from 'rxjs/operators';
import { IconService } from '../../../services/icon.service';

/** Compatible con StrapiSubComponentesNavegadorEntry (id opcional si viene del API) */
interface ItemNavegacion {
  id?: number;
  titulo?: string;
  redireccion?: string;
  paginaInicio: boolean;
}

@Component({
  selector: 'grafologia-barra-navegacion',
  imports: [LucideAngularModule],
  templateUrl: './barra-navegacion.component.html',
  styleUrl: './barra-navegacion.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarraNavegacionComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() items: ItemNavegacion[] = [];
  @Input() comportamiento: 'fixed' | 'observer' = 'fixed';
  @Input() seccionesObservadas = '';
  @Input() PaginaPrincipal = 'inicio';
  @ViewChild('navElement', { read: ElementRef, static: false })
  navElementRef!: ElementRef<HTMLElement>;
  private iconService = inject(IconService);

  logoSvg = computed(() =>
    this.iconService.getIcon('ScangraphOpcion2', {
      scale: 1,
      primaryColor: '#3A51D1',
      secondaryColor: '#3A51D1'
    })
  );

  readonly MenuIcon = Menu;
  readonly CloseIcon = X;
  readonly LogOutIcon = LogOut;

  #authStore = inject(AuthStore);
  private storage = inject(StorageService);

  username = computed(() => this.#authStore.user()?.username);
  isLoggedIn = computed(() => !!this.username());

  userInitials = computed(() => {
    const name = this.username();
    if (!name) return 'U';

    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  });

  avatarColor = computed(() => {
    const initials = this.userInitials();
    return this.generateColorFromString(initials);
  });

  avatarTextColor = computed(() => {
    const bgColor = this.avatarColor();
    return this.getContrastColor(bgColor);
  });

  isMobileMenuOpen = signal(false);
  navbarClass = signal<string>('transparent');
  lineClass = signal<string>('default');
  hasScrolled = signal(false);

  private readonly NAVBAR_HEIGHT = 75;
  private pendingScrollTarget: string | null = null;
  private observer: IntersectionObserver | null = null;
  private sectionsToObserve: string[] = [];
  private hasSectionsInPage = false;
  private navElement: HTMLElement | null = null;
  private lineElement: HTMLElement | null = null;
  private destroyRef = inject(DestroyRef);

  // 👇 NUEVO: ResizeObserver para detectar cambios de altura
  private resizeObserver?: ResizeObserver;

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isMobileMenuOpen.set(false);

        if (this.pendingScrollTarget) {
          const target = this.pendingScrollTarget;
          setTimeout(() => {
            this.scrollToElement(target);
            this.pendingScrollTarget = null;
          }, 100);
        }

        if (this.comportamiento === 'observer') {
          setTimeout(() => this.checkSectionsExistence(), 200);
        }
      });
  }

  private generateColorFromString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }

    const hue = Math.abs(hash % 360);
    const saturation = 45 + (Math.abs(hash) % 20);
    const lightness = 45 + (Math.abs(hash >> 8) % 15);

    const rgb = this.hslToRgb(hue / 360, saturation / 100, lightness / 100);

    const hex = '#' + rgb.map(x => {
      const hexValue = Math.round(x).toString(16);
      return hexValue.length === 1 ? '0' + hexValue : hexValue;
    }).join('');

    return hex;
  }

  private hslToRgb(h: number, s: number, l: number): number[] {
    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    return [r * 255, g * 255, b * 255];
  }

  private getContrastColor(hexColor: string): string {
    const hex = hexColor.replace('#', '');

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? '#1F2937' : '#FFFFFF';
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (!this.hasScrolled()) {
      this.hasScrolled.set(true);
      this.navbarClass.set('default');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const mobileMenu = this.elementRef.nativeElement.querySelector('.mobile-menu-panel');
    const toggleButton = this.elementRef.nativeElement.querySelector('.mobile-menu-toggle');

    if (this.isMobileMenuOpen() &&
        mobileMenu &&
        !mobileMenu.contains(target) &&
        !toggleButton.contains(target)) {
      this.closeMobileMenu();
    }
  }

  ngOnInit() {
    setTimeout(() => {
      this.navElement = this.elementRef.nativeElement.querySelector('.barra-navegacion');
      this.lineElement = this.elementRef.nativeElement.querySelector('.navbar-line');
    }, 0);

    if (this.seccionesObservadas) {
      this.sectionsToObserve = this.seccionesObservadas
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0 && s.toLowerCase() !== 'footer');
    }

    if (this.comportamiento === 'observer') {
      if (this.sectionsToObserve.length > 0) {
        setTimeout(() => this.checkSectionsExistence(), 100);
      } else {
        this.setInitialStyle('white', 'active');
      }
    } else {
      this.setInitialStyle('white', 'active');
      this.hasScrolled.set(true);
    }
  }

  // 👇 NUEVO: Lifecycle hook para configurar el observer después del render
  ngAfterViewInit() {
    this.setupHeightObserver();
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    // 👇 NUEVO: Limpiar el ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  // 👇 NUEVO: Método para observar cambios de altura del navbar
  private setupHeightObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.navElementRef?.nativeElement) {
        const height = this.navElementRef.nativeElement.offsetHeight;

        // Actualizar variable CSS global en el elemento <html>
        document.documentElement.style.setProperty('--navbar-height', `${height}px`);

        console.log('📏 Altura navbar actualizada:', height + 'px');
      }
    });

    // Observar el elemento nav
    if (this.navElementRef?.nativeElement) {
      this.resizeObserver.observe(this.navElementRef.nativeElement);

      // Ejecutar una vez inmediatamente para establecer el valor inicial
      const initialHeight = this.navElementRef.nativeElement.offsetHeight;
      document.documentElement.style.setProperty('--navbar-height', `${initialHeight}px`);
      console.log('📏 Altura inicial navbar:', initialHeight + 'px');
    }
  }

  toggleMobileMenu() {
    const newState = !this.isMobileMenuOpen();
    this.isMobileMenuOpen.set(newState);

    if (newState) {
      this.navbarClass.set('menu-open');
      this.lineClass.set('hidden');
      document.body.style.overflow = 'hidden';
    } else {
      this.closeMobileMenu();
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen.set(false);
    document.body.style.overflow = '';

    if (this.hasScrolled()) {
      this.navbarClass.set('default');
      this.lineClass.set('default');
    } else {
      this.navbarClass.set('transparent');
      this.lineClass.set('default');
    }
  }

  async onLogout(): Promise<void> {
    this.closeMobileMenu();

    try {
      const USER_EMAIL_KEY = 'USER_EMAIL_STRAPI_INTRANET';
      await this.storage.removeItem(USER_EMAIL_KEY);

      this.#authStore.logout();
      this.router.navigate(['/auth/login']);
    } catch (error: unknown) {
      console.error(error);
      this.router.navigate(['/auth/login']);
    }
  }

  private setInitialStyle(navClass: string, lineClassValue: string) {
    if (this.navElement) {
      this.renderer.setStyle(this.navElement, 'transition', 'none');
    }
    if (this.lineElement) {
      this.renderer.setStyle(this.lineElement, 'transition', 'none');
    }

    this.navbarClass.set(navClass);
    this.lineClass.set(lineClassValue);

    setTimeout(() => {
      if (this.navElement) {
        this.renderer.removeStyle(this.navElement, 'transition');
      }
      if (this.lineElement) {
        this.renderer.removeStyle(this.lineElement, 'transition');
      }
    }, 50);
  }

  private checkSectionsExistence() {
    let foundSections = 0;

    this.sectionsToObserve.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        foundSections++;
      }
    });

    this.hasSectionsInPage = foundSections > 0;

    if (this.hasSectionsInPage) {
      this.checkInitialScrollPosition();
    } else {
      this.setInitialStyle('white', 'active');
      this.hasScrolled.set(true);
    }
  }

  private checkInitialScrollPosition() {
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollPosition > 0) {
      this.hasScrolled.set(true);
    }

    let isInObservedSection = false;

    for (const sectionId of this.sectionsToObserve) {
      const element = document.getElementById(sectionId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrollPosition;

        const adjustedScrollPosition = scrollPosition + this.NAVBAR_HEIGHT + 1;
        const viewportHeight = window.innerHeight;
        const threshold = viewportHeight * 0.2;

        if (adjustedScrollPosition >= elementTop &&
            adjustedScrollPosition <= elementTop + threshold) {
          isInObservedSection = true;
          break;
        }
      }
    }

    if (scrollPosition > 0) {
      if (isInObservedSection) {
        this.setInitialStyle('white', 'active');
      } else {
        this.setInitialStyle('default', 'default');
      }
    } else {
      this.setInitialStyle('transparent', 'default');
    }

    setTimeout(() => {
      this.setupObserver();
    }, 100);
  }

  private setupObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }

    const visibleSections = new Set<string>();

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = entry.target.id;

          if (entry.isIntersecting) {
            visibleSections.add(sectionId);
          } else {
            visibleSections.delete(sectionId);
          }

          if (this.hasScrolled() && !this.isMobileMenuOpen()) {
            if (visibleSections.size > 0) {
              this.navbarClass.set('white');
              this.lineClass.set('active');
            } else {
              this.navbarClass.set('default');
              this.lineClass.set('default');
            }
          }
        });
      },
      {
        threshold: 0,
        rootMargin: `-${this.NAVBAR_HEIGHT + 1}px 0px -100% 0px`
      }
    );

    let observedCount = 0;
    this.sectionsToObserve.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element && this.observer) {
        this.observer.observe(element);
        observedCount++;
      }
    });

    if (observedCount === 0) {
      this.setInitialStyle('white', 'active');
      this.hasScrolled.set(true);
    }
  }

  onNavItemClick(item: ItemNavegacion, event: Event): void {
    event.preventDefault();

    if (this.isMobileMenuOpen()) {
      this.closeMobileMenu();
    }

    const redireccion = item.redireccion;
    if (!redireccion) return;

    if (!item.paginaInicio) {
      if (redireccion.startsWith('http://') || redireccion.startsWith('https://')) {
        window.location.href = redireccion;
      } else {
        this.router.navigate([redireccion]);
      }
      return;
    }

    const currentUrl = this.router.url;
    const targetUrl = `/${this.PaginaPrincipal}`;
    const currentSlug = currentUrl.split('/')[1]?.split('#')[0] || '';

    if (currentSlug === this.PaginaPrincipal) {
      this.scrollToElement(redireccion);
    } else {
      this.router.navigate([targetUrl], {
        fragment: redireccion
      }).then(() => {
        setTimeout(() => this.scrollToElement(redireccion), 500);
      });
    }
  }

  private scrollToElement(targetId: string): void {
    const element = targetId.startsWith('#')
      ? document.querySelector(targetId)
      : document.getElementById(targetId);

    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - this.NAVBAR_HEIGHT;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  onLogoClick(event: Event): void {
    event.preventDefault();

    if (this.isMobileMenuOpen()) {
      this.closeMobileMenu();
    }

    this.router.navigate([`/${this.PaginaPrincipal}`]).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  goToLogin() {
    this.closeMobileMenu();
    this.router.navigate(['auth/login']);
  }

  goToRegister() {
    this.closeMobileMenu();
    this.router.navigate(['auth/register']);
  }

  goToMyZone() {
    this.closeMobileMenu();
    this.router.navigate(['/private-area/mi-zona']);
  }

  goToMyProfile() {
    this.closeMobileMenu();
    this.router.navigate(['/private-area/mi-zona/mi-perfil']);
  }
}
